// Phase T2.0A Prompt 2 — Member-facing Event Info Card.
//
// Renders a clean, scannable list of event logistics — only fields that
// actually have values. Understands singles-vs-doubles capacity language so
// the count reads naturally to members ("14 Players Registered" vs
// "7 Teams Registered").

import React from 'react';
import { CalendarBlank, Clock, MapPin, Users, CurrencyDollar, Tag } from '@phosphor-icons/react';
import { playerCountLabel } from '../../lib/capacity';

function formatDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
}

function formatTimeRange(startIso, endIso) {
  if (!startIso) return null;
  const opts = { hour: 'numeric', minute: '2-digit' };
  const s = new Date(startIso).toLocaleTimeString(undefined, opts);
  if (!endIso) return s;
  const e = new Date(endIso).toLocaleTimeString(undefined, opts);
  return `${s} – ${e}`;
}

function formatFee(cents) {
  if (cents == null) return null;
  if (cents === 0) return 'Free';
  const dollars = cents / 100;
  return dollars % 1 === 0 ? `$${dollars.toFixed(0)}` : `$${dollars.toFixed(2)}`;
}

function feeLine(cents) {
  const fee = formatFee(cents);
  if (!fee) return null;
  if (fee === 'Free') return 'Free';
  return `${fee} per player`;
}

function capacityText({ eventType, entries = [], maxEntries }) {
  // Prompt 2.2: registered count is DISTINCT registered players (not row count).
  // Doubles cap = maxEntries * 2. Incomplete rows contribute 1 player, not 2.
  return playerCountLabel(entries, eventType, maxEntries);
}

function Row({ icon: Icon, label, value, testId }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-3 py-2" data-testid={testId}>
      <div className="w-8 h-8 rounded-full bg-white border border-[#e3dcd1] flex items-center justify-center shrink-0">
        <Icon size={14} weight="duotone" className="text-[#013523]" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-[#6a766e]">{label}</div>
        <div className="text-[14px] text-[#013523] font-medium truncate">{value}</div>
      </div>
    </div>
  );
}

export default function EventInfoCard({ event, eventType }) {
  const date = formatDate(event.starts_at);
  const time = formatTimeRange(event.starts_at, event.ends_at);
  const location = event.location_text || null;
  const format = event.match_format_text || null;
  const fee = feeLine(event.entry_fee_cents);
  const capacity = capacityText({
    eventType,
    entries: event.entries || [],
    maxEntries: event.max_entries || null,
  });

  // Only render the card if at least ONE row has content beyond the always-
  // shown capacity — otherwise the card is redundant.
  const rows = [date, time, location, format, fee].filter(Boolean);
  if (rows.length === 0 && (event.entries || []).length === 0) return null;

  return (
    <div className="border border-[#e3dcd1] rounded-2xl bg-white p-4 sm:p-5" data-testid="event-info-card">
      <div className="text-[11px] uppercase tracking-wider text-[#6a766e] mb-1">
        Event Info
      </div>
      <div className="divide-y divide-[#f3f1ec]">
        <Row icon={CalendarBlank} label="Date" value={date}    testId="event-info-date" />
        <Row icon={Clock}         label="Time" value={time}    testId="event-info-time" />
        <Row icon={MapPin}        label="Location" value={location} testId="event-info-location" />
        <Row icon={Tag}           label="Format"   value={format}   testId="event-info-format" />
        <Row icon={CurrencyDollar} label="Entry Fee" value={fee}    testId="event-info-fee" />
        <Row icon={Users}         label="Registered" value={capacity} testId="event-info-capacity" />
      </div>
    </div>
  );
}
